import { useCallback } from 'react'

import type { DeriveLoginHeader } from '@/lib/derive/sign-session-key'
import { signSessionKeyAction } from '@/lib/derive/sign-session-key'
import type { SelectionState } from '@/lib/derive/subaccount-selection'
import {
  deriveConfirmSessionKey,
  deriveLinkSubaccounts,
  deriveListSubaccounts,
  derivePrepareSessionKey,
} from '@/service/venue.api'
import type {
  DeriveLinkSubaccountsResponse,
  DeriveListSubaccountsResponse,
} from '@/types/generated/public_api'

export function useDeriveOnboard() {
  // Registers a session key over `subaccountIds` and nothing else. The list is
  // baked into the Action the customer signs and cannot be edited afterwards,
  // so it must already be the whole of what this wallet's connection needs to
  // trade — see `registrationScope`. Two wallet prompts: the typed data, then
  // the login timestamp that authenticates the registration call itself.
  //
  // walletAddress must already be connected (the caller owns the
  // connectWallet() call); this never opens the wallet picker itself.
  const registerSessionKey = useCallback(
    async (accountId: string, walletAddress: string, subaccountIds: string[]): Promise<void> => {
      const prepared = await derivePrepareSessionKey({
        accountId,
        walletAddress,
        subaccountIds,
      })

      // int64 arrives as a decimal string (ts-proto forceLong=string).
      const { actionSignature, loginTimestampMs, loginSignature } = await signSessionKeyAction({
        walletAddress,
        typedDataJson: prepared.typedDataJson,
        chainId: Number(prepared.chainId),
      })

      await deriveConfirmSessionKey({
        accountId,
        walletAddress,
        actionSignature,
        loginTimestampMs,
        loginSignature,
      })
    },
    []
  )

  // Lists the connected wallet's subaccounts for the picker, on the wallet's
  // own login header rather than a session key — which is what lets the
  // customer choose before anything is registered. The header also enriches
  // every id: a stored key only reads the subaccounts it was registered for.
  const listSubaccounts = useCallback(
    (
      accountId: string,
      walletAddress: string,
      login: DeriveLoginHeader
    ): Promise<DeriveListSubaccountsResponse> =>
      deriveListSubaccounts({ accountId, walletAddress, ...login }),
    []
  )

  // Submits the picker's selection: existing subaccounts to link plus how
  // many brand-new ones to create. Zero wallet signatures — the key registered
  // for this selection covers the links and the creates' funding transfer
  // server-side. Freshly created ids are the exception: they did not exist to
  // be named in the scope, so they come back needing their own registration.
  const linkSubaccounts = useCallback(
    (
      accountId: string,
      walletAddress: string,
      selection: SelectionState
    ): Promise<DeriveLinkSubaccountsResponse> =>
      deriveLinkSubaccounts({
        accountId,
        walletAddress,
        subaccountIds: selection.selected,
        createCount: selection.createCount,
        fundingSubaccountId: selection.fundingSubaccountId,
      }),
    []
  )

  return { registerSessionKey, listSubaccounts, linkSubaccounts }
}
